/* eslint-disable camelcase */
const React = require('react');

const { useState, useEffect } = React;
const { string, shape, number, bool, func } = require('prop-types');

const TextField = require('@andes/textfield');
const { Button } = require('@andes/button');
const { VisuallyHidden } = require('@andes/common');

const { track } = require('../helpers');
const { validateZipCode } = require('../helpers');

const namespace = 'zip-code';
const handleFindZipCodeClick = () => {
  track('hub', 'find_zipcode');
};

const ZipCode = ({
  placeholder,
  main_action,
  secondary_action,
  zipcode = '',
  errors = null,
  zipCodeValidations,
  autoFocus,
  hasAddresses,
  deviceType,
  isLoggedIn,
  translations,
  selectedAddress = null,
  zipCodeSubmitted = null,
  zipCodeChanged = null,
  webView,
  setShowSpinner,
  analytics,
  melidata,
}) => {
  const [zipCodeValue, setZipCodeValue] = useState(zipcode);
  const [errorMessage, setErrorMessage] = useState(errors && errors.zipcode);

  const isMobile = deviceType === 'mobile';

  const handleZipCodeChange = (value) => {
    setErrorMessage('');

    if (zipCodeChanged) {
      zipCodeChanged(value);
    }
    setZipCodeValue(value);
  };

  useEffect(() => {
    if (selectedAddress) {
      handleZipCodeChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  useEffect(() => {
    if (errors) {
      setErrorMessage(errors.zipcode);

      setShowSpinner(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleSubmitButtonClick = (event) => {
    const invalidField = validateZipCode(zipCodeValue, translations, zipCodeValidations);
    window.parent.postMessage({ type: 'nav:cp:redirect' }, '*');

    track(
      'hub',
      'add_zipcode',
      {
        zipcode: zipCodeValue,
        has_errors: !!invalidField,
        ...melidata?.event_data,
      },
      { ...analytics?.dimesions },
    );

    if (invalidField) {
      setErrorMessage(invalidField.errorMessage);
      event.preventDefault();
      return;
    }

    if (zipCodeSubmitted) {
      zipCodeSubmitted();
    }

    if (!webView) {
      setShowSpinner(true);
    }
  };

  const buttonUse = () => (
    <Button
      hierarchy={isMobile || !isLoggedIn ? 'loud' : 'quiet'}
      size="medium"
      type="submit"
      data-testid="button-use-zipcode"
      className={`${namespace}__use-button`}
      onClick={handleSubmitButtonClick}
    >
      {main_action.label}
    </Button>
  );

  const buttonFind = () => (
    <Button
      className={`${namespace}__find-button`}
      data-testid="find-button"
      hierarchy="transparent"
      href={secondary_action.action}
      size="medium"
      target={secondary_action.target}
      onClick={handleFindZipCodeClick}
    >
      {secondary_action.label}
    </Button>
  );

  const textfieldActions = () => {
    if (isMobile) {
      return null;
    }
    return buttonUse();
  };

  const externalActions = () => {
    if (isMobile) {
      return (
        <>
          {buttonUse()}
          {buttonFind()}
        </>
      );
    }

    return <>{buttonFind()}</>;
  };

  return (
    <div className={namespace}>
      <input type="hidden" name="hasAddresses" value={hasAddresses} />
      <TextField
        label={translations.labelCP}
        placeholder={placeholder}
        data-testid="zip-code-textfield"
        className={`${namespace}__textfield`}
        name="zipcode"
        modifier={errorMessage ? 'error' : undefined}
        helper={errorMessage}
        autoFocus={!!errorMessage || autoFocus}
        maxLength={zipCodeValidations.maxLength}
        embeddedChildren
        type="tel"
        autoComplete="off"
        value={zipCodeValue}
        onChange={(event) => handleZipCodeChange(event.target.value)}
      >
        {textfieldActions()}
        {errorMessage && <VisuallyHidden aria-live="polite" role="alert">{errorMessage}</VisuallyHidden> }
      </TextField>
      <div className={`${namespace}__secondary-actions`}>{externalActions()}</div>
    </div>
  );
};

ZipCode.propTypes = {
  placeholder: string.isRequired,
  zipcode: string,
  main_action: shape({
    type: string,
    label: string.isRequired,
    action: string.isRequired,
  }).isRequired,
  secondary_action: shape({
    type: string,
    label: string.isRequired,
    action: string.isRequired,
  }).isRequired,
  errors: shape({
    zipcode: string,
  }),
  zipCodeValidations: shape({
    maxLength: number,
  }).isRequired,
  autoFocus: bool.isRequired,
  deviceType: string.isRequired,
  hasAddresses: bool.isRequired,
  isLoggedIn: bool.isRequired,
  translations: shape({}).isRequired,
  selectedAddress: number,
  webView: bool.isRequired,
  zipCodeSubmitted: func,
  zipCodeChanged: func,
  setShowSpinner: func,
  analytics: shape({
    dimesions: shape({}),
  }),
  melidata: shape({
    event_data: shape({}),
  }),
};

module.exports = ZipCode;
